/**
 * Le **champ de date de filtre** — la pilule du dessin, et un calendrier
 * dessiné avec les mêmes jetons.
 *
 * Le porteur l'a demandé par son nom : **une date ne se tape jamais**. L'écran
 * Finance affichait « Journée comptable, au format AAAA-MM-JJ » au-dessus d'une
 * zone de texte nue, et demandait donc à un contrôleur financier de composer un
 * ISO 8601 à la main, plusieurs fois par jour. Chaque frappe est une occasion
 * de se tromper d'un chiffre — sur un écran où la date choisit *quelle journée
 * comptable* on lit, la faute est silencieuse : le serveur répond, la journée
 * est simplement une autre.
 *
 * ## Pourquoi pas `<input type="date">`
 *
 * Parce qu'il peint le calendrier du **navigateur** : ses couleurs, ses formes,
 * ses rayons, aucun jeton. À côté d'une pilule dessinée, il se voit emprunté.
 * Le calendrier est donc bâti, sur le système de design — et tout ce que le
 * natif donnait gratuitement est repris à la main, et éprouvé : un sélecteur de
 * date qui ne marcherait qu'à la souris serait **pire** que le champ de texte
 * qu'il remplace, puisque celui-là marchait au clavier.
 *
 * ## Le clavier, avant la souris
 *
 * `←` `→` d'un jour, `↑` `↓` d'une semaine, `Page préc.` / `Page suiv.` d'un
 * mois, `Début` et `Fin` aux extrémités de la semaine, `Entrée` choisit, `Échap`
 * ferme **et rend le focus au déclencheur**. Le panneau s'annonce comme une
 * boîte de dialogue nommée par son mois, et la grille se lit comme une grille :
 * en-têtes de colonne pour les initiales, une cellule par jour, l'état de
 * sélection porté par la cellule.
 *
 * ## Ce qui sort reste ISO — c'est ce que la route prend
 *
 * C'est le point à ne pas manquer : l'affichage est **localisé**, la valeur ne
 * l'est pas. Ce que l'opérateur lit est mis en forme dans sa locale — à Dakar,
 * `05/09/2026` — et ce qui part vers `GET /reconciliation/reports/{date}` est
 * `2026-09-05`, toujours. Ne jamais confondre les deux, et ne jamais envoyer ce
 * qui a été affiché : `05/09` et `09/05` sont deux journées comptables.
 *
 * ## Aucune règle que le serveur n'exprime
 *
 * Ni jours futurs grisés, ni week-ends fermés, ni borne haute ou basse : aucune
 * route ne dit rien de tel. Une borne inventée ici deviendrait une règle
 * comptable que personne n'a écrite, et le premier opérateur bloqué appellerait
 * le support pour un interdit qui n'existe pas. Le jour où une borne sera
 * nécessaire, elle viendra d'une valeur **servie**.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { FilterPill } from './FilterPill.js';
import {
  addDays,
  addMonths,
  dayOf,
  firstWeekDay,
  fromIsoDay,
  monthGrid,
  monthOf,
  toIsoDay,
} from './calendar-days.js';
import { useCloseOnOutsidePointer } from './use-dismiss.js';

/** Ce que le champ de date attend. */
export interface FilterDateFieldProps {
  /** L'identifiant du déclencheur, celui que le libellé de la pilule désigne. */
  readonly controlId: string;
  /** Clé de traduction du libellé. */
  readonly labelKey: string;
  /** Clé de traduction de l'invite — tant qu'aucune journée n'est choisie. */
  readonly placeholderKey: string;
  /** La journée choisie, en ISO `AAAA-MM-JJ`, ou la chaîne vide. */
  readonly value: string;
  /** Appelé avec la journée **en ISO**. Jamais avec ce qui est affiché. */
  readonly onSelect: (isoDay: string) => void;
  /** Le champ est-il en erreur ? */
  readonly invalid?: boolean;
  /** L'identifiant du message d'erreur, pour `aria-describedby`. */
  readonly describedBy?: string;
  /**
   * Le jour tenu pour « aujourd'hui ».
   *
   * Paramétrable pour que les contrôles puissent le fixer : un cas qui
   * dépendrait de l'horloge de la machine passerait au vert onze mois par an.
   * Aucun écran ne le passe — c'est l'horloge du poste, et elle ne choisit
   * rien : elle ne fait que marquer un jour.
   */
  readonly today?: string;
}

/** Le calendrier du produit. */
export function FilterDateField({
  controlId,
  labelKey,
  placeholderKey,
  value,
  onSelect,
  invalid,
  describedBy,
  today,
}: FilterDateFieldProps): ReactNode {
  const { t, i18n } = useTranslation();
  const declencheur = useRef<HTMLButtonElement | null>(null);
  const contenant = useRef<HTMLSpanElement | null>(null);
  const grille = useRef<HTMLDivElement | null>(null);
  const [ouvert, setOuvert] = useState(false);

  const locale = i18n.language.length > 0 ? i18n.language : 'fr';
  const aujourdhui = today ?? toIsoDay(new Date());
  /** Le jour qui porte le focus dans la grille. Le choisi, sinon aujourd'hui. */
  const [pointe, setPointe] = useState(() => (fromIsoDay(value) === null ? aujourdhui : value));

  const idPanneau = `${controlId}-panneau`;
  const idTitre = `${controlId}-mois`;

  // Les trois mises en forme viennent d'`Intl`, en UTC : la valeur est un jour,
  // pas un instant, et la lire dans le fuseau du poste la ferait reculer d'un
  // jour à l'est de Greenwich.
  const enLettres = useMemo(
    () => new Intl.DateTimeFormat(locale, { dateStyle: 'long', timeZone: 'UTC' }),
    [locale],
  );
  const enChiffres = useMemo(
    () => new Intl.DateTimeFormat(locale, { timeZone: 'UTC' }),
    [locale],
  );
  const enMois = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }),
    [locale],
  );
  const enInitiale = useMemo(
    () => new Intl.DateTimeFormat(locale, { weekday: 'narrow', timeZone: 'UTC' }),
    [locale],
  );
  const enJourDeSemaine = useMemo(
    () => new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' }),
    [locale],
  );

  /** Met en forme un jour ISO pour l'œil — cette chaîne ne repart jamais. */
  const misEnForme = useCallback(
    (iso: string, format: Intl.DateTimeFormat): string => {
      const instant = fromIsoDay(iso);
      return instant === null ? iso : format.format(instant);
    },
    [],
  );

  const affichee = fromIsoDay(value) === null ? t(placeholderKey) : misEnForme(value, enChiffres);
  const semaines = useMemo(() => monthGrid(pointe, firstWeekDay(locale)), [locale, pointe]);
  const moisAffiche = monthOf(pointe);

  const fermer = useCallback(() => {
    setOuvert(false);
  }, []);
  useCloseOnOutsidePointer(contenant, ouvert, fermer);

  /** Ferme et **rend le focus au déclencheur** — jamais l'un sans l'autre. */
  const fermerEtRevenir = useCallback(() => {
    setOuvert(false);
    declencheur.current?.focus();
  }, []);

  const choisir = useCallback(
    (iso: string) => {
      // L'ISO, toujours : ce qui a été affiché ne repart pas.
      onSelect(iso);
      setPointe(iso);
      fermerEtRevenir();
    },
    [fermerEtRevenir, onSelect],
  );

  // Le focus suit le jour pointé, tant que le panneau est ouvert : c'est le
  // patron du `tabindex` mobile — un seul jour atteignable à la tabulation, les
  // flèches déplacent le focus réel, et le lecteur d'écran annonce chaque jour.
  useEffect(() => {
    if (!ouvert) return;
    const cible = grille.current?.querySelector<HTMLButtonElement>(`[data-jour="${pointe}"]`);
    cible?.focus();
  }, [ouvert, pointe]);

  const auClavierDuDeclencheur = useCallback(
    (evenement: KeyboardEvent<HTMLButtonElement>) => {
      if (evenement.key === 'ArrowDown' || evenement.key === 'Enter' || evenement.key === ' ') {
        evenement.preventDefault();
        setPointe(fromIsoDay(value) === null ? aujourdhui : value);
        setOuvert(true);
        return;
      }
      if (evenement.key === 'Escape' && ouvert) {
        evenement.preventDefault();
        fermerEtRevenir();
      }
    },
    [aujourdhui, fermerEtRevenir, ouvert, value],
  );

  const auClavierDeLaGrille = useCallback(
    (evenement: KeyboardEvent<HTMLDivElement>) => {
      const deplacer = (iso: string): void => {
        evenement.preventDefault();
        setPointe(iso);
      };
      switch (evenement.key) {
        case 'ArrowLeft':
          return deplacer(addDays(pointe, -1));
        case 'ArrowRight':
          return deplacer(addDays(pointe, 1));
        case 'ArrowUp':
          return deplacer(addDays(pointe, -7));
        case 'ArrowDown':
          return deplacer(addDays(pointe, 7));
        case 'PageUp':
          return deplacer(addMonths(pointe, -1));
        case 'PageDown':
          return deplacer(addMonths(pointe, 1));
        case 'Home': {
          // Le début de la semaine AFFICHÉE, telle que la locale l'ordonne :
          // c'est la ligne que l'opérateur a sous les yeux.
          const ligne = semaines.find((semaine) => semaine.includes(pointe));
          return ligne === undefined ? undefined : deplacer(ligne[0]);
        }
        case 'End': {
          const ligne = semaines.find((semaine) => semaine.includes(pointe));
          return ligne === undefined ? undefined : deplacer(ligne[ligne.length - 1]);
        }
        case 'Escape':
          evenement.preventDefault();
          fermerEtRevenir();
          return undefined;
        default:
          return undefined;
      }
    },
    [fermerEtRevenir, pointe, semaines],
  );

  return (
    <FilterPill
      triggerId={controlId}
      labelId={`${controlId}-libelle`}
      valueId={`${controlId}-valeur`}
      labelKey={labelKey}
      display={affichee}
      panelId={idPanneau}
      expanded={ouvert}
      haspopup="dialog"
      invalid={invalid}
      describedBy={describedBy}
      triggerRef={declencheur}
      containerRef={contenant}
      onToggle={() => {
        if (ouvert) fermer();
        else {
          setPointe(fromIsoDay(value) === null ? aujourdhui : value);
          setOuvert(true);
        }
      }}
      onKeyDown={auClavierDuDeclencheur}
    >
      {ouvert ? (
        <div className="filter-panel filter-panel--calendar" id={idPanneau} role="dialog" aria-labelledby={idTitre}>
          <div className="calendar__header">
            <button
              className="calendar__nav"
              type="button"
              aria-label={t('calendar.previousMonth')}
              onClick={() => {
                setPointe(addMonths(pointe, -1));
              }}
            >
              <span aria-hidden="true">‹</span>
            </button>
            {/* Le titre nomme le panneau ET s'annonce à chaque feuilletage :
                sans région vivante, feuilleter au clavier changerait la grille
                sans que rien ne dise dans quel mois on vient d'entrer. */}
            <span className="calendar__title" id={idTitre} aria-live="polite">
              {misEnForme(pointe, enMois)}
            </span>
            <button
              className="calendar__nav"
              type="button"
              aria-label={t('calendar.nextMonth')}
              onClick={() => {
                setPointe(addMonths(pointe, 1));
              }}
            >
              <span aria-hidden="true">›</span>
            </button>
          </div>

          <div
            className="calendar__grid"
            ref={grille}
            role="grid"
            aria-labelledby={idTitre}
            onKeyDown={auClavierDeLaGrille}
          >
            <div className="calendar__week" role="row">
              {semaines[0].map((jour) => (
                <span
                  className="calendar__weekday"
                  key={`initiale-${jour}`}
                  role="columnheader"
                  // L'initiale seule — « L », « M », « M » — ne distingue pas
                  // lundi de mardi à l'oreille. Le nom complet est donc donné
                  // au lecteur d'écran, et l'initiale gardée pour l'œil.
                  aria-label={misEnForme(jour, enJourDeSemaine)}
                >
                  <span aria-hidden="true">{misEnForme(jour, enInitiale)}</span>
                </span>
              ))}
            </div>
            {semaines.map((semaine) => (
              <div className="calendar__week" key={`semaine-${semaine[0]}`} role="row">
                {semaine.map((jour) => {
                  const choisiJour = jour === value;
                  const dehors = monthOf(jour) !== moisAffiche;
                  return (
                    <span
                      className="calendar__cell"
                      key={jour}
                      role="gridcell"
                      aria-selected={choisiJour}
                    >
                      <button
                        className="calendar__day"
                        type="button"
                        data-jour={jour}
                        data-outside={dehors ? 'true' : undefined}
                        data-today={jour === aujourdhui ? 'true' : undefined}
                        data-selected={choisiJour ? 'true' : undefined}
                        aria-current={jour === aujourdhui ? 'date' : undefined}
                        // Le quantième seul — « 5 » — ne dit ni le mois ni
                        // l'année. Le nom accessible porte la date entière.
                        aria-label={misEnForme(jour, enLettres)}
                        tabIndex={jour === pointe ? 0 : -1}
                        onClick={() => {
                          choisir(jour);
                        }}
                      >
                        <span aria-hidden="true">{dayOf(jour)}</span>
                      </button>
                    </span>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </FilterPill>
  );
}
